import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import FoundingCardRender from '../components/FoundingCardRender';
import { captureShareCardNode, downloadBundle } from '../components/ShareCard';
import {
  FoundingCard, FoundingSummary, getCardByToken, getMyCard, getSummary, logFoundingEvent,
} from '../services/foundingClass';
import './FirstStudents.css';

const CARD_BASE = 'https://lomistudy.com/first-students';
const SHARE_BASE = 'https://lomistudy.com/fc'; // server-rendered OG + redirect to the card
const HOME_URL = '/'; // main Lomi

// Pre-fetch an external avatar to a data: URL so html-to-image's canvas capture isn't CORS-tainted
// (same mechanism as RankShareModal). Same-origin paths embed fine as-is.
async function fetchAvatarAsDataUrl(url: string): Promise<string | null> {
  try {
    if (url.startsWith('/')) return url;
    const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const FirstStudents: React.FC = () => {
  const [params] = useSearchParams();
  const { fc: fcPath } = useParams();
  const fc = fcPath || params.get('fc') || undefined;
  const src = params.get('src') || (fc ? 'direct' : 'web');

  const [card, setCard] = useState<FoundingCard | null>(null);
  const [summary, setSummary] = useState<FoundingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logFoundingEvent('page_view', { fc, source: src });
    (async () => {
      const c = fc ? await getCardByToken(fc, src) : await getMyCard();
      if (c) {
        setCard(c);
        if (c.avatar_url) fetchAvatarAsDataUrl(c.avatar_url).then(setAvatarDataUrl);
      } else {
        setSummary(await getSummary());
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = card ? `${SHARE_BASE}/${card.share_code}` : CARD_BASE;
  // Personalized share caption — only scale/scarcity flexes land on a stranger (overall rank, pioneer);
  // subject/strongest fall back to the class identity. (Copy placeholder pending messaging rethink.)
  const flex = card?.highlights?.[0];
  let shareText = 'Lomi Founding Class · 2018 E.C.';
  if (flex?.category === 'overall') shareText = `${flex.label} on Lomi`;
  else if (flex?.category === 'pioneer') shareText = flex.label.replace(/ · Member #\d+$/, '');

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2400); };

  // Off-screen capture card renders with the CORS-safe avatar (data URL, or same-origin path as-is).
  const captureCard: FoundingCard | null = card
    ? { ...card, avatar_url: avatarDataUrl ?? (card.avatar_url?.startsWith('/') ? card.avatar_url : null) }
    : null;

  async function capture() {
    return captureShareCardNode(captureRef.current!, `lomi-founding-${card?.member_number ?? ''}.png`);
  }

  // Primary: share the rendered card IMAGE via the OS sheet (TikTok / Telegram / WhatsApp / IG stories
  // receive the actual image, with their real logos). Falls back to download where file-share is absent.
  async function handleShare() {
    if (!captureRef.current || busy) return;
    setBusy('share');
    try {
      const b = await capture();
      const file = new File([b.blob], b.filename, { type: 'image/png' });
      const nav: any = navigator;
      if (nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: 'Lomi Founding Class', text: shareText });
      } else {
        downloadBundle(b);
        flash('Image saved — post it to your status');
      }
      logFoundingEvent('share_click', { fc: card?.share_code, source: src });
      URL.revokeObjectURL(b.objectUrl);
    } catch (e: any) {
      if (e?.name !== 'AbortError') flash('Could not prepare the image — try Save image');
    } finally { setBusy(''); }
  }

  async function handleSaveImage() {
    if (!captureRef.current || busy) return;
    setBusy('image');
    try {
      const b = await capture();
      downloadBundle(b);
      logFoundingEvent('card_download', { fc: card?.share_code, source: src });
      flash('Saved — post it to your status');
      URL.revokeObjectURL(b.objectUrl);
    } catch { flash('Could not save the image — try Copy link'); }
    finally { setBusy(''); }
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      logFoundingEvent('share_click', { fc: card?.share_code, source: 'copy' });
      flash('Link copied — paste it in any chat');
    } catch { flash(shareUrl); }
  }

  if (loading) {
    return <div className="fs-page"><div className="fs-eyebrow">Lomi Founding Class</div></div>;
  }

  const Header = () => (
    <a className="fs-topbar" href={HOME_URL} aria-label="Lomi home"><span>Lomi</span></a>
  );

  // ---- personal card ----
  if (card && captureCard) {
    return (
      <div className="fs-page">
        <div className="fc-preview"><FoundingCardRender card={card} /></div>

        <div className="fs-actions">
          <button className="fs-btn fs-btn-primary" onClick={handleShare} disabled={!!busy}>
            {busy === 'share' ? 'Preparing…' : 'Share'}
          </button>
          <button className="fs-btn fs-btn-ghost" onClick={handleSaveImage} disabled={!!busy}>
            {busy === 'image' ? 'Saving…' : 'Save image'}
          </button>
          <button className="fs-btn fs-btn-ghost" onClick={handleCopyLink} disabled={!!busy}>
            Copy link
          </button>
        </div>
        <div className="fs-hint">
          <b>Share</b> posts the card image to your status (TikTok, Instagram, Telegram) · <b>Copy link</b> for a chat.
        </div>

        <div className="fs-footlinks">
          <a href={HOME_URL}>Explore Lomi →</a>
          <a href="https://t.me/Lomistudybot" target="_blank" rel="noreferrer">Lomi on Telegram →</a>
        </div>

        {/* off-screen 1080×1920 node for capture (CORS-safe avatar) */}
        <div className="fc-capture-host" aria-hidden="true">
          <FoundingCardRender ref={captureRef} card={captureCard} />
        </div>
        {toast && <div className="fs-toast">{toast}</div>}
      </div>
    );
  }

  // ---- public page (no token / not a member / forwarded link) ----
  const s = summary;
  return (
    <div className="fs-page">
      <Header />
      <div className="fs-public">
        <div className="fs-eyebrow">Lomi Founding Class · Class of {s?.class_year ?? '2018 E.C.'}</div>
        <h1 className="fs-public-hero">The students who were here at the beginning.</h1>
        <p className="fs-public-sub">
          Lomi is AI-powered ESLCE exam prep, built in Ethiopia. In its very first season, this class made history.
        </p>
        <div className="fs-stats">
          <div className="fs-stat"><div className="fs-stat-n">{(s?.students_headline ?? 25000).toLocaleString()}+</div><div className="fs-stat-l">First students</div></div>
          <div className="fs-stat"><div className="fs-stat-n">{((s?.questions_answered ?? 2400000) / 1e6).toFixed(1)}M+</div><div className="fs-stat-l">Questions answered</div></div>
          <div className="fs-stat"><div className="fs-stat-n">{(s?.schools ?? 3861).toLocaleString()}</div><div className="fs-stat-l">Schools</div></div>
        </div>
        <p className="fs-quiet">There will be other classes. There will only ever be one first — and it is theirs.</p>
        <div className="fs-footlinks">
          <a href={HOME_URL}>Explore Lomi →</a>
          <a href="/testimonials">What students say →</a>
        </div>
      </div>
    </div>
  );
};

export default FirstStudents;
